import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBox,
  faCalendarDay,
  faClock,
  faBolt,
  faEye,
  faFilterCircleXmark,
  faInbox,
  faList,
  faMagnifyingGlass,
  faMap,
  faMapLocationDot,
  faMapMarkerAlt,
  faPlus,
  faPaperPlane,
  faRefresh,
  faRoute,
  faShieldHalved,
  faSliders,
  faStopwatch,
  faTableCells,
  faTriangleExclamation,
  faTruck,
  faUser,
  faUserTie,
  faWeightHanging
} from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { AvatarModule } from 'primeng/avatar';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { DialogModule } from 'primeng/dialog';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { LoadAvailabilityService } from '../load-availability.service';
import {
  LoadAvailability,
  LoadAvailabilityFilterOptions,
  LoadAvailabilitySearchParams
} from '../load-availability.model';
import { LoadTrustSummary, MarketplaceFilterOption } from '../../loads/load-request.model';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { Page } from '../../models/page.model';
import { GoogleMapComponent } from '../../shared/google-map/google-map.component';
import { LanguageService } from '../../services/language.service';

type SelectOption = { label: string; value: string };
type SearchSummaryItem = { label: string; value: string };

@Component({
  selector: 'app-availabilities-list',
  standalone: true,
  templateUrl: './availabilities-list.component.html',
  styleUrls: ['./availabilities-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    TagModule,
    CheckboxModule,
    AvatarModule,
    TableModule,
    PaginatorModule,
    TranslateModule,
    FontAwesomeModule,
    IftaLabelModule,
    DatePickerModule,
    TooltipModule,
    ToggleSwitch,
    DialogModule,
    GoogleMapComponent
  ]
})
export class AvailabilitiesListComponent {
  private readonly fb = inject(FormBuilder);
  private readonly loadAvailabilityService = inject(LoadAvailabilityService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly languageService = inject(LanguageService);
  protected readonly router = inject(Router);

  readonly icons = {
    box: faBox,
    bolt: faBolt,
    calendar: faCalendarDay,
    clock: faClock,
    eye: faEye,
    filterClear: faFilterCircleXmark,
    inbox: faInbox,
    list: faList,
    map: faMap,
    mapLocation: faMapLocationDot,
    marker: faMapMarkerAlt,
    plus: faPlus,
    send: faPaperPlane,
    refresh: faRefresh,
    route: faRoute,
    search: faMagnifyingGlass,
    shield: faShieldHalved,
    sliders: faSliders,
    stopwatch: faStopwatch,
    table: faTableCells,
    truck: faTruck,
    user: faUser,
    userTie: faUserTie,
    warning: faTriangleExclamation,
    weight: faWeightHanging
  };
  protected readonly loading = signal(false);
  protected readonly loadError = signal(false);
  protected readonly selectedMapAvailabilityId = signal<number | null>(null);
  protected readonly advancedSearchVisible = signal(false);
  protected readonly filterOptionsLoading = signal(false);

  // Filter signal to trigger updates
  protected readonly searchParams = signal<{ filters: LoadAvailabilitySearchParams; page: number; size: number }>({
    filters: { sort: 'id,desc', lang: this.languageService.currentLanguage() },
    page: 0,
    size: 10
  });

  // Main data signal
  public readonly loadAvailabilitiesResult = toSignal(
    toObservable(this.searchParams).pipe(
      tap(() => {
        this.loading.set(true);
        this.loadError.set(false);
      }),
      switchMap((params) =>
        this.loadAvailabilityService.search(params.filters, params.page, params.size).pipe(
          catchError(() => {
            this.loadError.set(true);
            return of(this.emptyPage(params.page, params.size));
          }),
          tap(() => this.restoreScrollPositionOnce()),
          finalize(() => this.loading.set(false))
        )
      )
    ),
    { initialValue: this.emptyPage(0, 10) }
  );

  protected readonly mapAvailabilities = computed(() => this.loadAvailabilitiesResult()?.content || []);
  protected readonly selectedMapAvailability = computed(() => {
    const availabilities = this.mapAvailabilities();
    const selectedId = this.selectedMapAvailabilityId();
    return (
      availabilities.find((availability) => availability.id === selectedId) ||
      availabilities[0] ||
      null
    );
  });

  readonly filtersForm: FormGroup = this.fb.group({
    searchText: [''],
    referenceNumber: [''],
    contactName: [null],
    origin: [''],
    destination: [''],
    date: [''],
    availableFrom: [''],
    availableTo: [''],
    createdAtFrom: [''],
    createdAtTo: [''],
    equipment: [''],
    minPallets: [''],
    maxWeight: [''],
    minRoutePrice: [''],
    maxRoutePrice: [''],
    currency: [null],
    visibility: [null],
    status: [null],
    openToNegotiation: [false],
    instantMatchEnabled: [false],
    hazmatCertified: [false],
    teamDrivers: [false],
    twic: [false],
    corridor: ['']
  });

  readonly equipmentOptions = signal<SelectOption[]>([]);
  readonly contactOptions = signal<SelectOption[]>([]);
  readonly originOptions = signal<SelectOption[]>([]);
  readonly destinationOptions = signal<SelectOption[]>([]);
  readonly visibilityOptions = signal<SelectOption[]>([]);
  readonly currencyOptions = signal<SelectOption[]>([]);
  readonly statusOptions = signal<SelectOption[]>([]);
  readonly perPageOptions = [10, 25, 50, 100].map((value) => ({ label: value.toString(), value }));
  activeView: 'List' | 'Map' = 'List';
  useTransportationAgencyFilter = false;
  useMyAvailabilitiesFilter = false;
  private readonly filterStorageKey = 'loadgate:private-availabilities:filters:v1';
  private readonly persistedDateFields = ['date', 'availableFrom', 'availableTo', 'createdAtFrom', 'createdAtTo'];
  private suppressFilterPersistence = false;
  private pendingScrollRestore: number | null = null;

  constructor() {
    this.restoreFilterState();
    this.loadFilterOptions();
    this.applyFilters(false);
    effect(() => {
      const lang = this.languageService.currentLanguage();
      this.loadFilterOptions();
      this.searchParams.update((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          lang
        }
      }));
    });
  }

  onPageChange(event: any): void {
    const page = event.page ?? Math.floor((event.first || 0) / (event.rows || 10));
    const size = event.rows ?? this.searchParams().size;

    if (page !== this.searchParams().page || size !== this.searchParams().size) {
      this.searchParams.update((prev) => ({
        ...prev,
        page: page,
        size: size
      }));
      this.pendingScrollRestore = 0;
      this.persistFilterState();
    }
  }

  applyFilters(resetPage = true): void {
    this.persistFilterState();
    const formValue = this.filtersForm.value;
    const filters: LoadAvailabilitySearchParams = {
      searchText: this.cleanText(formValue.searchText),
      referenceNumber: this.cleanText(formValue.referenceNumber),
      contactName: this.cleanText(formValue.contactName),
      corridorOrigin: this.cleanText(formValue.origin),
      corridorDestination: this.cleanText(formValue.destination),
      equipmentType: this.cleanText(formValue.equipment),
      hazmatCertified: formValue.hazmatCertified ? true : undefined,
      teamDrivers: formValue.teamDrivers ? true : undefined,
      availableDate: this.formatDate(formValue.date),
      availableFrom: this.formatDate(formValue.availableFrom),
      availableTo: this.formatDate(formValue.availableTo),
      createdAtFrom: this.formatDate(formValue.createdAtFrom),
      createdAtTo: this.formatDate(formValue.createdAtTo),
      minAvailablePallets: this.positiveNumber(formValue.minPallets),
      minMaxWeightLbs: this.positiveNumber(formValue.maxWeight),
      minRoutePrice: this.positiveNumber(formValue.minRoutePrice),
      maxRoutePrice: this.positiveNumber(formValue.maxRoutePrice),
      currency: this.cleanText(formValue.currency),
      visibility: this.cleanText(formValue.visibility),
      status: this.cleanText(formValue.status),
      openToNegotiation: formValue.openToNegotiation ? true : undefined,
      instantMatchEnabled: formValue.instantMatchEnabled ? true : undefined,
      sort: 'id,desc',
      lang: this.languageService.currentLanguage()
    };

    if (this.useTransportationAgencyFilter) {
      const company = this.sessionStorageService.getCompany();
      if (company?.transportationAgencyId) {
        filters.transportationAgencyId = company.transportationAgencyId;
      }
    }
    const createdBy = this.getActiveCreatedBy();
    if (createdBy) {
      filters.createdBy = createdBy;
    }

    this.selectedMapAvailabilityId.set(null);
    this.searchParams.set({
      filters: this.removeEmptyFilters(filters),
      page: resetPage ? 0 : this.searchParams().page,
      size: this.searchParams().size
    });
  }

  findAvailabilities(): void {
    this.applyFilters();
  }

  resetFilters(): void {
    this.clearPersistedFilterState();
    this.suppressFilterPersistence = true;
    this.filtersForm.reset({
      searchText: '',
      referenceNumber: '',
      contactName: null,
      origin: '',
      destination: '',
      date: '',
      availableFrom: '',
      availableTo: '',
      createdAtFrom: '',
      createdAtTo: '',
      equipment: '',
      minPallets: '',
      maxWeight: '',
      minRoutePrice: '',
      maxRoutePrice: '',
      currency: null,
      visibility: null,
      status: null,
      openToNegotiation: false,
      instantMatchEnabled: false,
      hazmatCertified: false,
      teamDrivers: false,
      twic: false,
      corridor: ''
    });
    this.useTransportationAgencyFilter = false;
    this.useMyAvailabilitiesFilter = false;
    this.applyFilters();
    this.suppressFilterPersistence = false;
  }

  toggleTransportationAgencyFilter(event: any): void {
    this.useTransportationAgencyFilter = event.checked;
    this.persistFilterState();
    this.applyFilters();
  }

  toggleMyAvailabilitiesFilter(): void {
    this.useMyAvailabilitiesFilter = !this.useMyAvailabilitiesFilter;
    this.persistFilterState();
    this.applyFilters();
  }

  setView(view: 'List' | 'Map'): void {
    this.activeView = view;
  }

  openAdvancedSearch(): void {
    this.advancedSearchVisible.set(true);
  }

  closeAdvancedSearch(): void {
    this.advancedSearchVisible.set(false);
  }

  applyAdvancedFilters(): void {
    this.applyFilters();
    this.closeAdvancedSearch();
  }

  refreshFilterOptions(): void {
    this.loadFilterOptions();
  }

  activeSearchSummary(): SearchSummaryItem[] {
    const formValue = this.filtersForm.value;
    const items: SearchSummaryItem[] = [];

    this.addSummaryItem(items, this.ui('Search', 'بحث'), this.cleanText(formValue.searchText));
    this.addSummaryItem(items, this.ui('Reference', 'المرجع'), this.cleanText(formValue.referenceNumber));
    this.addSummaryItem(items, this.ui('Contact', 'جهة الاتصال'), this.cleanText(formValue.contactName));
    this.addSummaryItem(items, this.ui('Origin', 'مصدر الاستلام'), this.cleanText(formValue.origin));
    this.addSummaryItem(items, this.ui('Destination', 'وجهة التسليم'), this.cleanText(formValue.destination));
    this.addSummaryItem(items, this.ui('Equipment', 'المعدة'), this.cleanText(formValue.equipment));
    this.addSummaryItem(items, this.ui('Date', 'التاريخ'), this.displayDate(formValue.date));
    this.addSummaryItem(items, this.ui('Available From', 'متاح من'), this.displayDate(formValue.availableFrom));
    this.addSummaryItem(items, this.ui('Available To', 'متاح إلى'), this.displayDate(formValue.availableTo));
    this.addSummaryItem(items, this.ui('Created From', 'أنشئ من'), this.displayDate(formValue.createdAtFrom));
    this.addSummaryItem(items, this.ui('Created To', 'أنشئ إلى'), this.displayDate(formValue.createdAtTo));
    this.addSummaryItem(items, this.ui('Min Pallets', 'أقل عدد منصات'), this.displayNumber(formValue.minPallets));
    this.addSummaryItem(items, this.ui('Min Weight', 'أقل سعة وزن'), this.displayNumber(formValue.maxWeight));
    this.addSummaryItem(items, this.ui('Min Rate', 'أقل سعر'), this.displayNumber(formValue.minRoutePrice));
    this.addSummaryItem(items, this.ui('Max Rate', 'أعلى سعر'), this.displayNumber(formValue.maxRoutePrice));
    this.addSummaryItem(items, this.ui('Currency', 'العملة'), this.cleanText(formValue.currency));
    this.addSummaryItem(items, this.ui('Visibility', 'الظهور'), this.cleanText(formValue.visibility));
    this.addSummaryItem(items, this.ui('Status', 'الحالة'), this.cleanText(formValue.status));

    if (formValue.hazmatCertified) {
      items.push({ label: this.ui('Requirement', 'المتطلب'), value: this.ui('Hazmat Certified', 'معتمد للمواد الخطرة') });
    }
    if (formValue.teamDrivers) {
      items.push({ label: this.ui('Requirement', 'المتطلب'), value: this.ui('Team Drivers', 'سائقان') });
    }
    if (formValue.openToNegotiation) {
      items.push({ label: this.ui('Pricing', 'التسعير'), value: this.ui('Negotiable', 'قابل للتفاوض') });
    }
    if (formValue.instantMatchEnabled) {
      items.push({ label: this.ui('Match', 'المطابقة'), value: this.ui('Instant Match', 'مطابقة فورية') });
    }
    if (this.useTransportationAgencyFilter) {
      items.push({ label: this.ui('Scope', 'النطاق'), value: this.ui('My Company', 'شركتي') });
    }
    if (this.useMyAvailabilitiesFilter) {
      items.push({ label: this.ui('Owner', 'المالك'), value: this.ui('My Trucks', 'شاحناتي') });
    }

    return items;
  }

  selectMapAvailability(availability: LoadAvailability): void {
    this.selectedMapAvailabilityId.set(availability.id);
  }

  openAvailability(id: number): void {
    this.persistFilterState();
    this.router.navigate(['/availability-details', id]);
  }

  signalBadges(availability: LoadAvailability): Array<{ icon: any; label: string; strong?: boolean }> {
    const signals = availability.signals;
    const badges: Array<{ icon: any; label: string; strong?: boolean }> = [
      { icon: this.icons.clock, label: this.ui(`Posted ${this.formatRelativeTime(availability.createdAt)}`, `نشر ${this.formatRelativeTime(availability.createdAt)}`) }
    ];
    if (!signals) {
      return badges;
    }
    if ((signals.viewCount || 0) > 0) {
      badges.push({ icon: this.icons.eye, label: this.ui(`Viewed ${signals.viewCount} times`, `شوهد ${signals.viewCount} مرة`) });
    }
    if ((signals.offersSubmittedCount || 0) > 0) {
      badges.push({ icon: this.icons.send, label: this.ui(`${signals.offersSubmittedCount} offers`, `${signals.offersSubmittedCount} عروض`) });
    }
    if ((signals.interestedUsersCount || 0) > 0) {
      badges.push({ icon: this.icons.user, label: this.ui(`${signals.interestedUsersCount} interested`, `${signals.interestedUsersCount} مهتم`) });
    }
    if (signals.hotLane) {
      badges.push({ icon: this.icons.bolt, label: this.ui('Hot lane', 'مسار نشط'), strong: true });
    }
    if (signals.fastPickup) {
      badges.push({ icon: this.icons.stopwatch, label: this.ui('Fast pickup', 'استلام سريع'), strong: true });
    }
    return badges;
  }

  trustBadgeLabel(trust?: LoadTrustSummary | null): string {
    return (
      trust?.company?.trustBadge ||
      this.labelize(trust?.company?.verificationStatus) ||
      this.labelize(trust?.customer?.verificationStatus) ||
      this.ui('Reviewed trust profile', 'ملف ثقة تمت مراجعته')
    );
  }

  ui(en: string, ar: string): string {
    return this.isArabic() ? ar : en;
  }

  isArabic(): boolean {
    return this.languageService.currentLanguage() === 'ar';
  }

  labelize(value?: string | null): string {
    if (!value) {
      return '';
    }
    if (this.isArabic()) {
      const ar = this.arabicEnumLabels()[this.normalizeEnumKey(value)];
      if (ar) {
        return ar;
      }
    }
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  protected carrierName(availability: LoadAvailability): string {
    if (availability.transportationAgencyId) {
      return availability.carrierName?.trim() || this.ui(`Carrier #${availability.transportationAgencyId}`, `ناقل #${availability.transportationAgencyId}`);
    }

    return this.ui('Independent Driver', 'سائق مستقل');
  }

  protected carrierInitial(availability: LoadAvailability): string {
    return this.carrierName(availability).charAt(0).toUpperCase() || 'C';
  }

  onLocationOptionFilter(type: 'origin' | 'destination' | 'equipment', event: unknown): void {
    const term = this.extractFilterTerm(event);
    if (!term) {
      this.setLazyOptions(type, []);
      return;
    }

    const params =
      type === 'origin'
        ? { origin: term }
        : type === 'destination'
          ? { destination: term }
          : { equipmentType: term };

    this.loadAvailabilityService.getFilterOptions({ ...params, lang: this.languageService.currentLanguage() }).subscribe({
      next: (options) => {
        const values =
          type === 'origin'
            ? options.origins
            : type === 'destination'
              ? options.destinations
              : options.equipmentTypes;
        const displayOptions =
          type === 'origin'
            ? options.originOptions
            : type === 'destination'
              ? options.destinationOptions
              : options.equipmentTypeOptions;
        this.setLazyOptions(type, this.toSelectOptions(displayOptions ?? values));
      },
      error: () => this.setLazyOptions(type, [])
    });
  }

  private cleanText(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length ? value.trim() : undefined;
  }

  private extractFilterTerm(event: unknown): string {
    const filter = (event as { filter?: unknown })?.filter;
    return typeof filter === 'string' ? filter.trim() : '';
  }

  private setLazyOptions(type: 'origin' | 'destination' | 'equipment', options: SelectOption[]): void {
    if (type === 'origin') {
      this.originOptions.set(options);
      return;
    }

    if (type === 'destination') {
      this.destinationOptions.set(options);
      return;
    }

    this.equipmentOptions.set(options);
  }

  private formatDate(value: unknown): string | undefined {
    if (!(value instanceof Date)) {
      return undefined;
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatRelativeTime(value?: string | null): string {
    if (!value) {
      return this.ui('recently', 'مؤخراً');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.ui('recently', 'مؤخراً');
    }
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return this.ui('just now', 'الآن');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return this.ui(`${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`, `قبل ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.ui(`${hours} ${hours === 1 ? 'hour' : 'hours'} ago`, `قبل ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`);
    const days = Math.floor(hours / 24);
    if (days < 30) return this.ui(`${days} ${days === 1 ? 'day' : 'days'} ago`, `قبل ${days} ${days === 1 ? 'يوم' : 'أيام'}`);
    const months = Math.floor(days / 30);
    if (months < 12) return this.ui(`${months} ${months === 1 ? 'month' : 'months'} ago`, `قبل ${months} ${months === 1 ? 'شهر' : 'أشهر'}`);
    const years = Math.floor(months / 12);
    return this.ui(`${years} ${years === 1 ? 'year' : 'years'} ago`, `قبل ${years} ${years === 1 ? 'سنة' : 'سنوات'}`);
  }

  private displayDate(value: unknown): string | undefined {
    return this.formatDate(value);
  }

  private displayNumber(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    return String(value);
  }

  private positiveNumber(value: unknown): number | undefined {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
  }

  protected formatDisplayDate(value?: string | null, includeYear = false): string {
    if (!value) {
      return this.ui('N/A', 'غير متوفر');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString(this.isArabic() ? 'ar-SA-u-ca-gregory' : undefined, {
      month: 'short',
      day: 'numeric',
      year: includeYear ? 'numeric' : undefined
    });
  }

  protected statusLabel(value?: string | null): string {
    return this.isArabic() ? (this.arabicEnumLabels()[this.normalizeEnumKey(value)] ?? this.labelize(value)) : (value || 'OPEN');
  }

  protected formatPallets(value: number | string | null | undefined): string {
    return value == null || value === '' ? '' : this.ui(`${value} pallets`, `${value} منصة`);
  }

  protected formatWeightLbs(value: number | null | undefined): string {
    return value == null ? '' : this.ui(`${value.toLocaleString()} lb`, `${value.toLocaleString()} رطل`);
  }

  private persistFilterState(): void {
    if (this.suppressFilterPersistence || typeof localStorage === 'undefined') {
      return;
    }

    const serialized = Object.fromEntries(
      Object.entries(this.filtersForm.value).map(([key, value]) => [key, this.serializeFilterValue(value)])
    );

    localStorage.setItem(this.filterStorageKey, JSON.stringify({
      filters: serialized,
      useTransportationAgencyFilter: this.useTransportationAgencyFilter,
      useMyAvailabilitiesFilter: this.useMyAvailabilitiesFilter,
      page: this.searchParams().page,
      size: this.searchParams().size,
      scrollY: this.pendingScrollRestore ?? this.currentScrollY()
    }));
  }

  private restoreFilterState(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const raw = localStorage.getItem(this.filterStorageKey);
    if (!raw) {
      return;
    }

    try {
      const saved = JSON.parse(raw) as {
        filters?: Record<string, unknown>;
        useTransportationAgencyFilter?: boolean;
        useMyAvailabilitiesFilter?: boolean;
        page?: number;
        size?: number;
        scrollY?: number;
      };
      const restored = Object.fromEntries(
        Object.entries(saved.filters ?? {}).map(([key, value]) => [key, this.deserializeFilterValue(key, value)])
      );

      this.filtersForm.patchValue(restored, { emitEvent: false });
      this.useTransportationAgencyFilter = !!saved.useTransportationAgencyFilter;
      this.useMyAvailabilitiesFilter = !!saved.useMyAvailabilitiesFilter;
      this.searchParams.update((prev) => ({
        ...prev,
        page: Number.isFinite(saved.page) ? Number(saved.page) : prev.page,
        size: Number.isFinite(saved.size) ? Number(saved.size) : prev.size
      }));
      this.pendingScrollRestore = Number.isFinite(saved.scrollY) ? Number(saved.scrollY) : null;
      this.seedPersistedSelectOptions(restored);
    } catch {
      localStorage.removeItem(this.filterStorageKey);
    }
  }

  private clearPersistedFilterState(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.filterStorageKey);
    }
  }

  private serializeFilterValue(value: unknown): unknown {
    return value instanceof Date ? this.formatDate(value) : value;
  }

  private deserializeFilterValue(key: string, value: unknown): unknown {
    if (!this.persistedDateFields.includes(key) || typeof value !== 'string' || !value) {
      return value;
    }

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date;
  }

  private seedPersistedSelectOptions(restored: Record<string, unknown>): void {
    this.originOptions.set(this.seedOption(this.originOptions(), restored['origin']));
    this.destinationOptions.set(this.seedOption(this.destinationOptions(), restored['destination']));
    this.equipmentOptions.set(this.seedOption(this.equipmentOptions(), restored['equipment']));
    this.contactOptions.set(this.seedOption(this.contactOptions(), restored['contactName']));
  }

  private seedOption(options: SelectOption[], value: unknown): SelectOption[] {
    if (typeof value !== 'string' || !value.trim() || options.some(option => option.value === value)) {
      return options;
    }
    return [{ label: value, value }, ...options];
  }

  private restoreScrollPositionOnce(): void {
    const scrollY = this.pendingScrollRestore;
    if (scrollY === null || typeof window === 'undefined') {
      return;
    }

    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'auto' });
      this.pendingScrollRestore = null;
    });
  }

  private currentScrollY(): number {
    return typeof window === 'undefined' ? 0 : window.scrollY || 0;
  }

  private normalizeEnumKey(value?: string | null): string {
    return (value ?? '').trim().replace(/[-\s]+/g, '_').replace(/:+/g, '').toUpperCase();
  }

  private arabicEnumLabels(): Record<string, string> {
    return {
      OPEN: 'مفتوح',
      AVAILABLE: 'متاح',
      ACTIVE: 'نشط',
      POSTED: 'منشور',
      BOOKED: 'محجوز',
      UNAVAILABLE: 'غير متاح',
      CANCELLED: 'ملغي',
      EXPIRED: 'منتهي',
      VERIFIED: 'موثق',
      UNVERIFIED: 'غير موثق',
      LIMITED_HISTORY: 'سجل محدود',
      COMMUNICATION_ONLY: 'تواصل فقط',
      MARKETPLACE_LEAD: 'عميل من السوق',
      TRUSTED: 'موثوق',
      EMERGING: 'في البداية',
      ESTABLISHING: 'قيد البناء'
    };
  }

  private getActiveCreatedBy(): number | undefined {
    if (!this.useMyAvailabilitiesFilter) {
      return undefined;
    }

    const user = this.sessionStorageService.getUser();
    return user?.id ?? user?.rowId ?? undefined;
  }

  private removeEmptyFilters(filters: LoadAvailabilitySearchParams): LoadAvailabilitySearchParams {
    return Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ) as LoadAvailabilitySearchParams;
  }

  private loadFilterOptions(): void {
    this.filterOptionsLoading.set(true);
    this.loadAvailabilityService
      .getFilterOptions({ lang: this.languageService.currentLanguage() })
      .pipe(finalize(() => this.filterOptionsLoading.set(false)))
      .subscribe({
        next: (options) => this.applyFilterOptions(options),
        error: () => undefined
      });
  }

  private applyFilterOptions(options: LoadAvailabilityFilterOptions): void {
    this.contactOptions.set(this.toSelectOptions(options.contactNames));
    this.originOptions.set(this.toSelectOptions(options.originOptions ?? options.origins));
    this.destinationOptions.set(this.toSelectOptions(options.destinationOptions ?? options.destinations));
    this.equipmentOptions.set(this.toSelectOptions(options.equipmentTypeOptions ?? options.equipmentTypes));
    this.visibilityOptions.set(this.toSelectOptions(options.visibilities));
    this.currencyOptions.set(this.toSelectOptions(options.currencies));
    this.statusOptions.set(this.toSelectOptions(options.statuses));
  }

  private toSelectOptions(values: Array<string | MarketplaceFilterOption> | null | undefined): SelectOption[] {
    const seen = new Set<string>();
    return (values ?? [])
      .map((value) => (typeof value === 'string' ? { label: value, value } : value))
      .filter((option) => option?.value?.trim())
      .filter((option) => {
        const key = option.value.trim();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map((option) => ({ label: option.label?.trim() || option.value.trim(), value: option.value.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private addSummaryItem(items: SearchSummaryItem[], label: string, value: string | undefined): void {
    if (!value) {
      return;
    }

    items.push({ label, value });
  }

  private emptyPage(page: number, size: number): Page<LoadAvailability> {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      size,
      number: page
    };
  }
}
